import {
  createContext,
  useContext,
  useState,
  useEffect,
  Component,
  type ReactNode,
  type ErrorInfo,
} from 'react'
import type { OrgItem } from '@/lib/apiTypes'
import { useOrgs } from '@/lib/hooks'
import { AppErrorScreen } from '@/app/AppErrorScreen'
import { reportBootstrapError } from '@/lib/bootstrapError'

// ── Types ─────────────────────────────────────────────────────────────────────

interface SelectedOrgContextValue {
  selectedSlug: string | null
  setSelectedSlug: (slug: string) => void
  selectedOrg: OrgItem | null
  orgs: OrgItem[]
  /** Resolves with the fresh list once re-fetched (null on failure; never rejects).
   *  Await it before navigating to a just-created org so OrgLayout can validate the slug. */
  refetchOrgs: () => Promise<{ orgs: OrgItem[] } | null>
  orgsLoading: boolean
  /** A background refetch is in flight (data retained). */
  orgsRefreshing: boolean
  orgsError: string | null
}

// ── Context ───────────────────────────────────────────────────────────────────

const SelectedOrgContext = createContext<SelectedOrgContextValue | null>(null)

// ── Persistence ───────────────────────────────────────────────────────────────

const STORAGE_KEY = 'tm-selected-org'

function readStoredSlug(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function writeStoredSlug(slug: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, slug)
  } catch {
    /* storage unavailable (e.g. Safari private browsing) */
  }
}

// ── Error boundary ────────────────────────────────────────────────────────────

interface ErrorBoundaryState {
  error: Error | null
}

class ContextErrorBoundary extends Component<
  { children: ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[SelectedOrgContext] Uncaught error:', error, info)
    reportBootstrapError('page-render')
  }

  render(): ReactNode {
    if (this.state.error) {
      // This boundary wraps the whole authenticated <Outlet/> subtree, so any
      // page render throw lands here (not just org-context failures) - the
      // stage reflects that; the diagnostic's path/stack pinpoint the page.
      return (
        <AppErrorScreen
          stage="page-render"
          error={this.state.error}
          action={{ label: 'Reload', onClick: () => window.location.reload() }}
        />
      )
    }
    return this.props.children
  }
}

// ── Provider ──────────────────────────────────────────────────────────────────

function SelectedOrgProviderInner({ children }: { children: ReactNode }) {
  const { data, loading, refreshing, error, refetch } = useOrgs()
  const orgs: OrgItem[] = data?.orgs ?? []

  const [selectedSlug, setSelectedSlug] = useState<string | null>(null)

  useEffect(() => {
    if (loading || orgs.length === 0) return

    // Auto-initialise: data transitioned from null → populated and no slug chosen yet.
    // Prefer the previously-selected org, but only if it's still in the user's list.
    //
    // This only fires on org-agnostic routes (Dashboard / first load with no URL slug).
    // On org-scoped routes the URL is the source of truth: OrgLayout calls
    // setSelectedSlug(orgSlug) to override whatever this picks. We deliberately do NOT
    // auto-select newly-added orgs here - create/demo-join flows navigate explicitly,
    // which lets OrgLayout drive the selection and avoids fighting the URL.
    if (selectedSlug === null) {
      const stored = readStoredSlug()
      const restored =
        stored && orgs.some((o) => o.slug === stored) ? stored : orgs[0].slug
      setSelectedSlug(restored)
    }
  }, [orgs, loading, selectedSlug])

  // Persist the active selection so it can be restored on the next app load.
  useEffect(() => {
    if (selectedSlug) writeStoredSlug(selectedSlug)
  }, [selectedSlug])

  const selectedOrg: OrgItem | null =
    orgs.find((o) => o.slug === selectedSlug) ?? null

  const value: SelectedOrgContextValue = {
    selectedSlug,
    setSelectedSlug,
    selectedOrg,
    orgs,
    refetchOrgs: refetch,
    orgsLoading: loading,
    orgsRefreshing: refreshing,
    orgsError: error,
  }

  return (
    <SelectedOrgContext.Provider value={value}>
      {children}
    </SelectedOrgContext.Provider>
  )
}

export function SelectedOrgProvider({ children }: { children: ReactNode }) {
  return (
    <ContextErrorBoundary>
      <SelectedOrgProviderInner>{children}</SelectedOrgProviderInner>
    </ContextErrorBoundary>
  )
}

// ── Consumer hook ─────────────────────────────────────────────────────────────

export function useSelectedOrg(): SelectedOrgContextValue {
  const ctx = useContext(SelectedOrgContext)
  if (!ctx) {
    throw new Error('useSelectedOrg must be used within a SelectedOrgProvider')
  }
  return ctx
}
